
import { getOwnerTabPreferencesWithOrder, updateTabOrderPreferences } from './CloseAPIs';

export const FIXED_TABS = ['HOME'];

const tabPreferencesCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes


export const saveTabOrder = async (ownerId, tabOrder) => {
  try {
    
    tabPreferencesCache.delete(ownerId);
    
    const tabOrderData = tabOrder.map((tab, index) => ({
      tab: tab,
      order_index: String(index)
    }));


    return new Promise((resolve, reject) => {
      updateTabOrderPreferences(ownerId, tabOrderData, 
        function onSuccess(result) {
          tabPreferencesCache.clear();
          resolve(result);
        }, 
        function onFailure(error) {
          console.error('Failed to save tab order to database:', error);
          reject(error);
        }
      );
    });
  } catch (error) {
    console.error('Failed to save tab order:', error);
    throw error;
  }
};


export const getTabOrder = async (ownerId, forceRefresh = false) => {
  try {
    const cacheKey = ownerId;
    if (!forceRefresh) {
      const cached = tabPreferencesCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        return cached.data;
      }
    }

    
    return new Promise((resolve, reject) => {
      getOwnerTabPreferencesWithOrder(ownerId, 
        function onSuccess(result) {
          try {
            const preferences = result.data.getOwnerTabPreferencesWithOrder;
            if (preferences && preferences.items) {
              const orderPreferences = preferences.items
                .filter(item => item.tab_order_index !== null && item.tab_order_index !== undefined)
                .sort((a, b) => parseInt(a.tab_order_index) - parseInt(b.tab_order_index))
                .map(item => item.tab);
              
              
              tabPreferencesCache.set(cacheKey, {
                data: orderPreferences.length > 0 ? orderPreferences : null,
                timestamp: Date.now()
              });
              
              resolve(orderPreferences.length > 0 ? orderPreferences : null);
            } else {
              resolve(null);
            }
          } catch (parseError) {
            console.error('Failed to parse tab preferences from API:', parseError);
            reject(parseError);
          }
        },
        function onFailure(error) {
          console.error('Failed to get tab order from API:', error);
          reject(error);
        }
      );
    });
  } catch (error) {
    console.error('Failed to get tab order from API:', error);
    throw error;
  }
};


export const applyTabOrder = (tabs, customOrder) => {
  if (!customOrder || !Array.isArray(customOrder) || customOrder.length === 0) {
    return tabs;
  }

  const reorderedTabs = [];
  const remainingTabs = [...tabs];

  customOrder.forEach(tabId => {
    const tabIndex = remainingTabs.findIndex(tab => 
      (tab.id === tabId) || (tab.label === tabId) || (tab === tabId)
    );
    if (tabIndex !== -1) {
      reorderedTabs.push(remainingTabs[tabIndex]);
      remainingTabs.splice(tabIndex, 1);
    }
  });

  reorderedTabs.push(...remainingTabs);

  return reorderedTabs;
};


export const getVisibleTabNames = (defaultTabs, customTabs, hiddenTabs = []) => {
  const visibleDefaultTabs = defaultTabs
    .filter(tab => !hiddenTabs.includes(tab.id))
    .map(tab => tab.id);
  
  const visibleCustomTabs = customTabs
    .filter(tab => !hiddenTabs.includes(tab.tab))
    .map(tab => tab.tab);

  return [...visibleDefaultTabs, ...visibleCustomTabs];
};


export const getReorderableTabs = (defaultTabs, customTabs, hiddenTabs = []) => {
  const visibleDefaultTabs = defaultTabs
    .filter(tab => !hiddenTabs.includes(tab.id));
  
  const visibleCustomTabs = customTabs
    .filter(tab => !hiddenTabs.includes(tab.tab));

  const fixedTabs = visibleDefaultTabs
    .filter(tab => FIXED_TABS.includes(tab.id))
    .map(tab => ({ id: tab.id, name: tab.name || tab.id, type: 'fixed' }));

  const reorderableDefaultTabs = visibleDefaultTabs
    .filter(tab => !FIXED_TABS.includes(tab.id))
    .map(tab => ({ id: tab.id, name: tab.name || tab.id, type: 'default' }));

  const reorderableCustomTabs = visibleCustomTabs
    .map(tab => ({ id: tab.tab, name: tab.tab, type: 'custom' }));

  return {
    fixedTabs,
    reorderableTabs: [...reorderableDefaultTabs, ...reorderableCustomTabs]
  };
};


export const applyTabOrderWithFixedTabs = (tabs, customOrder) => {
  if (!customOrder || !Array.isArray(customOrder) || customOrder.length === 0) {
    return tabs;
  }


  const fixedTabs = [];
  const reorderableTabs = [];

  tabs.forEach(tab => {
    const tabId = tab.id || tab.label || tab;
    if (FIXED_TABS.includes(tabId)) {
      fixedTabs.push(tab);
    } else {
      reorderableTabs.push(tab);
    }
  });


  const orderedReorderableTabs = [];
  
  customOrder.forEach(tabId => {
    const tabIndex = reorderableTabs.findIndex(tab => 
      (tab.id === tabId) || (tab.label === tabId) || (tab === tabId)
    );
    if (tabIndex !== -1) {
      orderedReorderableTabs.push(reorderableTabs[tabIndex]);
      reorderableTabs.splice(tabIndex, 1);
    }
  });

  orderedReorderableTabs.push(...reorderableTabs);


  const result = [...fixedTabs, ...orderedReorderableTabs];
  
  return result;
};


export const cleanupTabOrder = async (ownerId, currentVisibleTabs) => {
  try {
    const savedOrder = await getTabOrder(ownerId);
    if (!savedOrder) return;

    const cleanedOrder = savedOrder.filter(tabId => 
      currentVisibleTabs.includes(tabId)
    );

    const newTabs = currentVisibleTabs.filter(tabId => 
      !savedOrder.includes(tabId)
    );

    if (cleanedOrder.length !== savedOrder.length || newTabs.length > 0) {
      await saveTabOrder(ownerId, [...cleanedOrder, ...newTabs]);
    }
  } catch (error) {
    console.warn('Failed to cleanup tab order:', error);
  }
};
